/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import Cookies from 'js-cookie';
import router from '@/router';
import identityApi from '@/services/identity-api';
import scountApi from '@/services/scount-api';

const state = {
  profile: {},
  emptyProfile: null,
  authToken: null,
  authStatus: null,
  accessToken: null,
  accessAttributes: [],
  isAuthenticated: null,
  isMobileApp: null,
  deviceId: null,
  questionnaire: {},
  questionnaireStatus: null,
  questionnaireErrorCode: null,
  questionnaireErrorDetail: null,
  avatarStatus: null,
  emailCheck: null,
  emailCheckStatus: null,
  restrictions: null,
  codeDetails: null,
  codeStatus: null,
  profileBlockedStatus: null,
  code: null,
  hasParticipantActiveDeviceSession: null,
  hasParticipantActiveDeviceSessionToFirstJune: null,
  // Extended authentication states
  extendedAuthRequest: null,
  extendedAuthRequestStatus: null,
  extendedAuthProcessStatus: null,
  extendedAuthProcessResult: null,
  extendedAuthChangeEmailStatus: null,
  extendedAuthChangeEmailResult: null,
  extendedAuthSubmitCodeStatus: null,
  extendedAuthSubmitCodeResult: null,
  extendedAuthResendCodeStatus: null,
  extendedAuthCancelStatus: null,
  providersAuth: null,
  providersAuthStatus: null,
};

const mutations = {
  removeUser() {
    Cookies.remove('token');
    localStorage.removeItem('returnFromGuide');
    if (router && router.currentRoute.meta.requiresAuth) {
      const returnUrl = `${window.location.origin}${router.currentRoute.path}`;
      window.location.href = `${window.location.origin}/?loginReturnUrl=${returnUrl}`;
    }
  },
  userBlocked(state) {
    Cookies.remove('token');
    localStorage.removeItem('returnFromGuide');
    const email = state.profile ? state.profile.emailAddress || '' : '';
    window.location.href = `${window.location.origin}/auth?showLoginBlocked=${email || 'true'}`;
  },
  setProfile(state, profile) {
    state.profile = profile || {};
    state.profile.fullName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
  },
  setEmptyProfile(state, empty) {
    state.emptyProfile = empty;
  },
  setAuthToken(state, value) {
    state.authToken = value || '';
  },
  setAuthStatus(state, value) {
    state.authStatus = value;
  },
  setAccessToken(state, value) {
    state.accessToken = value || '';
  },
  setAccessAttributes(state, value) {
    state.accessAttributes = value || [];
  },
  setAuthenticated(state, value) {
    state.isAuthenticated = value;
  },
  setMobileApp(state, value) {
    state.isMobileApp = value;
  },
  setDeviceId(state, value) {
    state.deviceId = value;
  },
  setQuestionnaire(state, value) {
    state.questionnaire = value || {};
  },
  setQuestionnaireStatus(state, value) {
    state.questionnaireStatus = value;
  },
  setQuestionnaireErrorCode(state, value) {
    state.questionnaireErrorCode = value;
  },
  setQuestionnaireErrorDetail(state, value) {
    state.questionnaireErrorDetail = value;
  },
  setAvatarStatus(state, value) {
    state.avatarStatus = value;
  },
  setProfileBlockedStatus(state, value) {
    state.profileBlockedStatus = value;
  },
  setEmailCheck(state, value) {
    state.emailCheck = value;
  },
  setEmailCheckStatus(state, value) {
    state.emailCheckStatus = value;
  },
  setRestrictions(state, value) {
    state.restrictions = value || {};
  },
  setCodeDetails(state, value) {
    state.codeDetails = value || {};
  },
  setCodeStatus(state, value) {
    state.codeStatus = value;
  },
  setCode(state, value) {
    state.code = value;
  },
  setHasDeviceSession(state, value) {
    state.hasParticipantActiveDeviceSession = value;
  },
  setHasDeviceSessionToFirstJune(state, value) {
    state.hasParticipantActiveDeviceSessionToFirstJune = value;
  },
  // Extended authentication mutations
  setExtendedAuthRequest(state, value) {
    state.extendedAuthRequest = value;
  },
  setExtendedAuthRequestStatus(state, value) {
    state.extendedAuthRequestStatus = value;
  },
  setExtendedAuthProcessStatus(state, value) {
    state.extendedAuthProcessStatus = value;
  },
  setExtendedAuthProcessResult(state, value) {
    state.extendedAuthProcessResult = value;
  },
  setExtendedAuthChangeEmailStatus(state, value) {
    state.extendedAuthChangeEmailStatus = value;
  },
  setExtendedAuthChangeEmailResult(state, value) {
    state.extendedAuthChangeEmailResult = value;
  },
  setExtendedAuthSubmitCodeStatus(state, value) {
    state.extendedAuthSubmitCodeStatus = value;
  },
  setExtendedAuthSubmitCodeResult(state, value) {
    state.extendedAuthSubmitCodeResult = value;
  },
  setExtendedAuthResendCodeStatus(state, value) {
    state.extendedAuthResendCodeStatus = value;
  },
  setExtendedAuthCancelStatus(state, value) {
    state.extendedAuthCancelStatus = value;
  },
  setProvidersAuth(state, value) {
    state.providersAuth = value;
  },
  setProvidersAuthStatus(state, value) {
    state.providersAuthStatus = value;
  },
};

const getters = {
  profile: (state) => state.profile,
  profileAvatar: (state) => (state.profile ? state.profile.avatar || null : null),
  emptyProfile: (state) => state.emptyProfile,
  authToken: (state) => state.authToken,
  authStatus: (state) => state.authStatus,
  accessToken: (state) => state.accessToken,
  accessAttributes: (state) => state.accessAttributes,
  isAuthenticated: (state) => state.isAuthenticated,
  isMobileApp: (state) => state.isMobileApp,
  deviceId: (state) => state.deviceId,
  questionnaire: (state) => state.questionnaire,
  questionnaireStatus: (state) => state.questionnaireStatus,
  questionnaireErrorCode: (state) => state.questionnaireErrorCode,
  questionnaireErrorDetail: (state) => state.questionnaireErrorDetail,
  avatarStatus: (state) => state.avatarStatus,
  emailCheck: (state) => state.emailCheck,
  emailCheckStatus: (state) => state.emailCheckStatus,
  restrictions: (state) => state.restrictions,
  codeDetails: (state) => state.codeDetails,
  codeStatus: (state) => state.codeStatus,
  profileBlockedStatus: (state) => state.profileBlockedStatus,
  code: (state) => state.code,
  hasParticipantActiveDeviceSession: (state) => state.hasParticipantActiveDeviceSession,
  hasParticipantActiveDeviceSessionToFirstJune: (state) => state.hasParticipantActiveDeviceSessionToFirstJune,
  needsConsent: (state) => {
    // Проверяем, нужно ли показать модальное окно согласий
    const { questionnaire, profile } = state;

    // Если есть анкета с ответами, проверяем её
    if (questionnaire && questionnaire.answers) {
      const { answers } = questionnaire;

      // Ищем ответы на вопросы согласий (в анкете используются ключи с верхним регистром)
      const sopdDistributionAnswer = answers.find((answer) => answer.key === 'SOPDDistribution');
      const sopdOfferAnswer = answers.find((answer) => answer.key === 'SOPDOffer');

      // Если хотя бы одно из согласий не дано (false или отсутствует), показываем модальное окно
      const needsDistribution = !sopdDistributionAnswer || sopdDistributionAnswer.value === 'false';
      const needsOffer = !sopdOfferAnswer || sopdOfferAnswer.value === 'false';

      return needsDistribution || needsOffer;
    }

    // Если анкеты нет, проверяем профиль (в профиле используются ключи с нижним регистром)
    if (profile && profile.agreements) {
      const { agreements } = profile;
      const needsDistribution = agreements.sopdDistribution === false;
      const needsOffer = agreements.sopdOffer === false;

      return needsDistribution || needsOffer;
    }

    return false;
  },
  // Extended authentication getters
  extendedAuthRequest: (state) => state.extendedAuthRequest,
  extendedAuthRequestStatus: (state) => state.extendedAuthRequestStatus,
  extendedAuthProcessStatus: (state) => state.extendedAuthProcessStatus,
  extendedAuthProcessResult: (state) => state.extendedAuthProcessResult,
  extendedAuthChangeEmailStatus: (state) => state.extendedAuthChangeEmailStatus,
  extendedAuthChangeEmailResult: (state) => state.extendedAuthChangeEmailResult,
  extendedAuthSubmitCodeStatus: (state) => state.extendedAuthSubmitCodeStatus,
  extendedAuthSubmitCodeResult: (state) => state.extendedAuthSubmitCodeResult,
  extendedAuthResendCodeStatus: (state) => state.extendedAuthResendCodeStatus,
  extendedAuthCancelStatus: (state) => state.extendedAuthCancelStatus,
  providersAuth: (state) => state.providersAuth,
  providersAuthStatus: (state) => state.providersAuthStatus,
};

const actions = {
  startAuth({ commit }, {
    type, emailAddress, russiaRunningToken, attributes,
  }) {
    return identityApi.startAuth({
      type, emailAddress, russiaRunningToken, attributes,
    })
      .then((response) => {
        commit('setAuthStatus', response.status);
        if (response.status === 200) {
          commit('setAuthToken', response.data.token);
        } else {
          commit('setAuthToken', null);
        }
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        const { code } = error.response.data;
        commit('setCodeDetails', code || '');
        commit('setAuthStatus', status || 0);
        commit('setAuthToken', null);
      });
  },
  approveAuth({ commit }, { token, code }) {
    return identityApi.approveAuth({ token, code })
      .then((response) => {
        commit('setAuthStatus', response.status);
        if (response.status === 200) {
          Cookies.set('token', response.data.accessToken || '', { expires: 365 });
          commit('setAccessToken', response.data.accessToken);
          commit('setAccessAttributes', response.data.attributes);
          commit('setAuthenticated', true);
        } else {
          commit('setAccessToken', null);
          commit('setAccessAttributes', []);
        }
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        commit('setAuthStatus', status || 0);
        commit('setAccessToken', null);
        commit('setAccessAttributes', []);
      });
  },
  getCodeDetails({ commit }, { code }) {
    return scountApi.getCodeDetails({ code })
      .then((response) => {
        commit('setCodeStatus', response.status);
        if (response.status === 200) {
          commit('setCodeDetails', response.data);
        } else {
          commit('setCodeDetails', null);
        }
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        commit('setCodeStatus', status || 0);
        commit('setCodeDetails', null);
      });
  },
  setIsMobileApp({ commit }, { value }) {
    commit('setMobileApp', value);
  },
  setDeviceId({ commit }, { value }) {
    commit('setDeviceId', value);
  },
  logout({ commit }) {
    commit('removeUser');
    window.location.href = `${window.location.origin}/`;
  },
  getProfile({ commit, dispatch }) {
    return scountApi.getProfile()
      .then((response) => {
        if (response.status === 200) {
          commit('setProfile', response.data);
          commit('setAuthenticated', true);
          commit('setEmptyProfile', false);
        }
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        if (status === 404) {
          commit('setAuthenticated', true);
          commit('setEmptyProfile', true);
        } else if (status === 401) {
          commit('setAuthenticated', false);
        } else if (status === 409) {
          // Когда "закрытая" МП и метод получения профиля вернул 409-й код,
          // тогда выполнить logout().
          const isPrivate = this.getters['settings/isPrivate'];
          if (isPrivate === true) dispatch('logout');
        }
      });
  },
  profileBlocked({ commit }) {
    return scountApi.profileBlocked()
      .then((response) => {
        commit('setProfileBlockedStatus', response.status);
        commit('removeUser');
        window.location.href = `${window.location.origin}/?accountDeleted=true`;
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        commit('setProfileBlockedStatus', status || 0);
      });
  },
  setAvatar({ commit }, { avatarType, file }) {
    return scountApi.setAvatar({ avatarType, file })
      .then((response) => {
        commit('setAvatarStatus', response.status);
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        commit('setAvatarStatus', status || 0);
      });
  },
  checkByEmail({ commit }, { emailAddress }) {
    return scountApi.checkByEmail({ emailAddress })
      .then((response) => {
        commit('setEmailCheckStatus', response.status);
        if (response.status === 200) {
          commit('setEmailCheck', response.data);
        } else {
          commit('setEmailCheck', null);
        }
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        commit('setEmailCheckStatus', status || 0);
      });
  },
  getQuestionnaire({ commit }) {
    return scountApi.getQuestionnaire()
      .then((response) => {
        commit('setQuestionnaireStatus', response.status);
        if (response.status === 200) {
          commit('setQuestionnaire', response.data);
        } else {
          commit('setQuestionnaireStatus', response.status);
        }
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        commit('setQuestionnaireStatus', status || 0);
      });
  },
  createQuestionnaire({ commit }) {
    return scountApi.createQuestionnaire()
      .then((response) => {
        commit('setQuestionnaireStatus', response.status);
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        commit('setQuestionnaireStatus', status || 0);
      });
  },
  applyAnswers({ commit }, answers) {
    return scountApi.applyAnswers({ answers })
      .then((response) => {
        commit('setQuestionnaireStatus', response.status);
        commit('setQuestionnaireErrorCode', null);
        commit('setQuestionnaireErrorDetail', null);
      })
      .catch((error) => {
        const data = error.response && error.response.data ? error.response.data : null;
        const status = error.response && error.response.status;
        const code = data ? data.code || null : null;
        const detail = data ? data.detail || null : null;
        commit('setQuestionnaireStatus', status || 0);
        commit('setQuestionnaireErrorCode', code);
        commit('setQuestionnaireErrorDetail', detail);
      });
  },
  completeQuestionnaire({ commit }) {
    return scountApi.completeQuestionnaire()
      .then((response) => {
        commit('setQuestionnaireStatus', response.status);
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        commit('setQuestionnaireStatus', status || 0);
      });
  },
  getRestrictions({ commit }) {
    return scountApi.getRestrictions()
      .then((response) => {
        if (response.status === 200) {
          commit('setRestrictions', response.data);
        } else {
          commit('setRestrictions', null);
        }
      })
      .catch(() => {
        commit('setRestrictions', null);
      });
  },
  getCode({ commit }) {
    return scountApi.getCode()
      .then((response) => {
        if (response.status === 200) {
          commit('setCode', response.data);
        } else {
          commit('setCode', null);
        }
      })
      .catch(() => {
        commit('setCode', null);
      });
  },
  hasDeviceSession({ commit }, { participantId }) {
    return scountApi.hasDeviceSession({ participantId })
      .then((response) => {
        if (response.status === 200) {
          commit('setHasDeviceSession', response.data.hasParticipantActiveDeviceSession);
          commit('setHasDeviceSessionToFirstJune', response.data.hasParticipantActiveDeviceSessionToFirstJune);
        } else {
          commit('setHasDeviceSession', null);
          commit('setHasDeviceSessionToFirstJune', null);
        }
      })
      .catch(() => {
        commit('setHasDeviceSession', null);
        commit('setHasDeviceSessionToFirstJune', null);
      });
  },
  // Extended authentication actions
  getProvidersAuth({ commit }) {
    return identityApi.getProvidersAuth()
      .then((response) => {
        commit('setProvidersAuthStatus', response.status);
        if (response.status === 200) {
          commit('setProvidersAuth', response.data);
        } else {
          commit('setProvidersAuth', null);
        }
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        commit('setProvidersAuthStatus', status || 0);
        commit('setProvidersAuth', null);
      });
  },
  createAuthRequest({ commit }, { providerType }) {
    return identityApi.createAuthRequest({ providerType })
      .then((response) => {
        commit('setExtendedAuthRequestStatus', response.status);
        if (response.status === 200) {
          commit('setExtendedAuthRequest', response.data);
        } else {
          commit('setExtendedAuthRequest', null);
        }
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        const data = error.response && error.response.data;
        commit('setExtendedAuthRequestStatus', status || 0);
        commit('setExtendedAuthRequest', data || null);
      });
  },
  processAuthRequest({ commit }, { token, attributes }) {
    return identityApi.processAuthRequest({ token, attributes })
      .then((response) => {
        commit('setExtendedAuthProcessStatus', response.status);
        if (response.status === 200) {
          commit('setExtendedAuthProcessResult', response.data);
        } else {
          commit('setExtendedAuthProcessResult', null);
        }
        return response;
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        const data = error.response && error.response.data;
        commit('setExtendedAuthProcessStatus', status || 0);
        commit('setExtendedAuthProcessResult', data || null);
        throw error;
      });
  },
  changeEmail({ commit }, { token, emailAddress }) {
    return identityApi.changeEmail({ token, emailAddress })
      .then((response) => {
        commit('setExtendedAuthChangeEmailStatus', response.status);
        // clear previous error/result on success
        commit('setExtendedAuthChangeEmailResult', null);
        return response;
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        const data = error.response && error.response.data;
        commit('setExtendedAuthChangeEmailStatus', status || 0);
        commit('setExtendedAuthChangeEmailResult', data || null);
        throw error;
      });
  },
  submitCode({ commit }, { token, code }) {
    return identityApi.submitCode({ token, code })
      .then((response) => {
        commit('setExtendedAuthSubmitCodeStatus', response.status);
        if (response.status === 200) {
          commit('setExtendedAuthSubmitCodeResult', response.data);
        } else {
          commit('setExtendedAuthSubmitCodeResult', null);
        }
        return response;
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        const data = error.response && error.response.data;
        commit('setExtendedAuthSubmitCodeStatus', status || 0);
        commit('setExtendedAuthSubmitCodeResult', data || null);
        throw error;
      });
  },
  resendCode({ commit }, { token }) {
    return identityApi.resendCode({ token })
      .then((response) => {
        commit('setExtendedAuthResendCodeStatus', response.status);
        return response;
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        commit('setExtendedAuthResendCodeStatus', status || 0);
        throw error;
      });
  },
  cancelAuthRequest({ commit }, { token }) {
    return identityApi.cancelAuthRequest({ token })
      .then((response) => {
        commit('setExtendedAuthCancelStatus', response.status);
        return response;
      })
      .catch((error) => {
        const status = error.response && error.response.status;
        commit('setExtendedAuthCancelStatus', status || 0);
        throw error;
      });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
